<script setup lang="ts">
import TheBottomSheet from '@/components/TheBottomSheet.vue'
import { computed, ref, watch } from 'vue'
import { T } from '@tolgee/vue'
import { useConfig } from '@/composables/game/config'
import { DAILY_TASK_ID } from '@/types/shared/player'
import { formatNumberSemiTruncate } from '@/helpers/utils'
import CheckIcon from '@/components/icons/CheckIcon.vue'
import CoinIcon from '@/components/icons/CoinIcon.vue'
import PlusIcon from '@/components/icons/PlusIcon.vue'
import BaseLinkButton from '@/components/base/BaseLinkButton.vue'
import { useDailyTransactionTask } from '@/composables/game/tasks'
import { useWalletConnect } from '@/composables/game/wallet-connect'
import { useTonConnectModal } from '@townsquarelabs/ui-vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { useConfetti } from '@/composables/game/confetti'
import { useFortuneWheel } from '@/composables/game/fortune-wheel'
import { syncPlayer, usePlayer } from '@/composables/game/player'
import TransactionFailedModal from '@/components/modals/TransactionFailedModal.vue'
import api from '@/services/api'
import { useToaster } from '@/composables/game/toasters'
import { useLocalStorage } from '@vueuse/core'
import { buildDropeeStorageKey } from '@/config/config'
import NumberFormatTooltip from '@/components/NumberFormatTooltip.vue'

const props = defineProps<{
  show: boolean
  isDone?: boolean
}>()
defineEmits(['hide'])
const { config } = useConfig()
const { open } = useTonConnectModal()
const { openTonkeeperInStore } = useDailyTransactionTask()
const { rawAddress, doLingoRaffleTx } = useWalletConnect()
const { showConfetti } = useConfetti()
const { updateState: updateFortuneWheelState } = useFortuneWheel()
const { claimTask } = usePlayer()
const { notify } = useToaster()

const shouldShowOffer = false

const coinBenefit = computed(() => config.value.game.tasks.dailyTasks[DAILY_TASK_ID.TON_DAILY_CHECKIN_LEGACY].reward)
const spinBenefit = computed(() => config.value.game.tasks.dailyTasks[DAILY_TASK_ID.TON_DAILY_CHECKIN_LEGACY].spins)
const hasFailed = ref(false)
const transactionDone = ref(Boolean(props.isDone))
const isLoading = ref(false)
const isClaiming = ref(false)
const transactionIsInProgress = useLocalStorage(buildDropeeStorageKey('daily_ton_transaction_in_progress'), false)
const showTransactionFailedModal = ref(false)
const firstClaimFailed = ref(false)

function onConnect() {
  open()
}

async function onSuccessfulClaim() {
  transactionIsInProgress.value = false
  showConfetti()
  transactionDone.value = true
  await syncPlayer()
  await updateFortuneWheelState()
}

async function onTransaction() {
  firstClaimFailed.value = false
  isLoading.value = true
  transactionIsInProgress.value = true
  try {
    await doLingoRaffleTx('0:4ae644e9a8d8f27a1590d874dbd7c17c360b12ea9d7442b368e79e588156b315', shouldShowOffer ? '8000000' : '9000000', shouldShowOffer)
    const success = await claimTask(DAILY_TASK_ID.TON_DAILY_CHECKIN_LEGACY)
    if (!success) {
      firstClaimFailed.value = true
      return
    }
    await onSuccessfulClaim()
  } catch (e) {
    hasFailed.value = true
  } finally {
    isLoading.value = false
  }
}

async function onReClaim() {
  isClaiming.value = true
  try {
    const hasDoneTx = await api.hasDoneDailyTx()
    if (hasDoneTx) {
      const success = await claimTask(DAILY_TASK_ID.TON_DAILY_CHECKIN_LEGACY)
      if (success) {
        firstClaimFailed.value = false
        await onSuccessfulClaim()
        return
      }
    }
    throw new Error()
  } catch (e) {
    notify({ type: 'error', title: 'Incomplete. Try again' })
  } finally {
    isClaiming.value = false
  }
}

watch(hasFailed, hasFailed => {
  if (hasFailed) {
    showTransactionFailedModal.value = true
  }
})

watch(() => props.isDone, async (isDone) => {
  if (isDone) {
    transactionDone.value = true
  }
}, { immediate: true })

watch(() => props.show, show => {
  if (show && transactionIsInProgress.value && !transactionDone.value) {
    firstClaimFailed.value = true
  }
})
</script>

<template>
  <TheBottomSheet
    :show="show"
    outer-content-class="[background-image:url('@/assets/images/gold-bg.png')]"
    content-class="pt-0 [background-image:url('@/assets/images/gold-bg.png')] flex flex-col items-center max-w-full overflow-x-hidden"
    close-button-class="text-black z-[10]"
    @hide="$emit('hide')"
  >
    <div class="mt-[48px] relative size-[88px] min-w-[88px]">
      <img
        class="absolute size-[176px] -left-[120px] -top-[88px] object-cover max-w-fit z-[3]"
        src="@/assets/images/toncoin1.png"
      >
      <img
        class="w-full h-full object-cover z-[2] relative"
        src="@/assets/images/gifs/duck-money-eye.gif"
      >
      <img
        class="absolute size-[176px] object-cover max-w-fit left-[18px] -top-[56px] z-[1]"
        src="@/assets/images/toncoin2.png"
      >
      <img
        class="absolute size-[162px] object-cover max-w-fit z-[1] -top-[130px] left-[74px] -rotate-[12deg]"
        src="@/assets/images/toncoin3.png"
      >
    </div>
    <div
      class="mt-0.5 w-full flex flex-col items-center text-black max-[320px]:gap-y-6"
      :class="firstClaimFailed ? 'gap-y-6' : 'gap-y-8'"
    >
      <div class="flex flex-col gap-y-2 items-center">
        <div class="text-center text-[32px] leading-8 font-bold -tracking-[0.64px]">
          <T
            v-if="transactionDone"
            key-name="tasks.daily_transaction_modal.title_success"
            default-value="Transaction succesful!"
          />
          <T
            v-else
            key-name="tasks.daily_transaction_modal.title"
            :default-value="shouldShowOffer ? '💸 Do a Transaction, For free!' : 'Daily check-in on TON'"
          />
        </div>
        <div class="max-w-[328px] text-base font-semibold text-center">
          <T
            key-name="tasks.daily_transaction_modal.description"
            :default-value="shouldShowOffer ? `Make a small transaction today and in less than 48 hours we’ll return your spend as a thank-you! 🤑` : 'Make a small daily TON transaction (just a few cents) and receive massive rewards!'"
          />
        </div>
      </div>
      <div class="flex flex-col gap-y-3 items-center w-max">
        <div
          v-if="coinBenefit"
          class="benefit-box benefit-box-glow"
        >
          <CheckIcon
            v-if="transactionDone"
            class="benefit-box-check scale-in-medium"
          />
          <CoinIcon class="w-5 h-auto min-w-[20px]" />
          <NumberFormatTooltip
            :number="coinBenefit"
            :formatting-type="'full'"
          >
            <span
              class="benefit-box-value"
            >{{ formatNumberSemiTruncate(coinBenefit) }}</span>
          </NumberFormatTooltip>
        </div>
        <PlusIcon
          v-if="coinBenefit && spinBenefit"
          class="text-white size-4"
        />
        <div
          v-if="spinBenefit"
          class="benefit-box benefit-box-glow"
        >
          <CheckIcon
            v-if="transactionDone"
            class="benefit-box-check scale-in-medium"
          />
          <img
            src="@/assets/images/fortune-wheel-without-border.png"
            class="size-8 min-w-[28px] object-cover"
          >
          <span class="benefit-box-value">
            <T
              key-name="fortune_wheel.info.more_spins.count_spin"
              default-value="{count, plural, one {# spin} other {# spins}}"
              :params="{count: spinBenefit}"
            />
          </span>
        </div>
      </div>
      <div class="w-full flex flex-col items-center gap-y-3">
        <div class="text-sm leading-4 font-medium text-black/60 flex items-center justify-center flex-wrap">
          <template v-if="firstClaimFailed">
            <T
              key-name="lingo.giveaway.transaction.validation_failed_description"
              default-value="Sometimes we need to double-check on the blockchain if your transaction was successful:"
            />
          </template>
          <template v-else>
            We recommend using &nbsp;
            <BaseLinkButton
              text-class="text-sm text-black  border-black group-active:border-b-black pt-0 pb-0"
              button-class="text-black"
              @click="openTonkeeperInStore"
            >
              Tonkeeper
            </BaseLinkButton>
            for transactions.
          </template>
        </div>
        <div
          class="w-full sticky bottom-0 rounded-xl flex flex-col gap-y-3"
        >
          <BaseButton
            v-if="firstClaimFailed && !!rawAddress"
            class="w-full scale-up-light shadow-btn"
            :loading="isClaiming"
            @click="onReClaim"
          >
            <T
              v-if="!isClaiming"
              key-name="lingo.giveaway.transaction.confirm_transaction"
              default-value="Confirm your transaction"
            />
            <T
              v-else
              key-name="lingo.giveaway.transaction.confirming_cta"
              default-value="Confirming..."
            />
          </BaseButton>

          <BaseButton
            v-if="transactionDone"
            class="w-full"
            button-class="!bg-light-stone !text-black/[.67]"
            @click="$emit('hide')"
          >
            <T
              key-name="tasks.daily_reward.come_back"
              default-value="Come back tomorrow"
            />
          </BaseButton>
          <BaseButton
            v-else-if="!rawAddress"
            class="w-full"
            :class="{'scale-up-light shadow-btn': !firstClaimFailed}"
            @click="onConnect"
          >
            <template #leftIcon>
              <img
                src="@/assets/images/gifs/money.gif"
                class="h-full w-auto max-w-[24px]"
              >
            </template>
            <T
              key-name="wallet.connect_wallet"
              default-value="Connect wallet"
            />
          </BaseButton>
          <BaseButton
            v-else
            :style-type="firstClaimFailed ? 'tertiary' : 'primary'"
            class="w-full"
            :class="{'scale-up-light shadow-btn': !firstClaimFailed}"
            :loading="isLoading"
            @click="onTransaction"
          >
            <T
              v-if="isLoading"
              key-name="lingo.giveaway.transaction.loading_cta"
              default-value="Processing..."
            />
            <T
              v-else-if="firstClaimFailed"
              key-name="lingo.giveaway.transaction.cta_start_new_transaction"
              default-value="Start new transaction"
            />
            <T
              v-else
              key-name="lingo.giveaway.transaction.cta"
              default-value="Complete Transaction"
            />
          </BaseButton>
        </div>
      </div>
    </div>
  </TheBottomSheet>

  <TransactionFailedModal
    :show="showTransactionFailedModal"
    @hide="showTransactionFailedModal = false"
  />
</template>

<style scoped lang="scss">
@import "@/assets/styles/ecommerce";

.shadow-btn {
  box-shadow: 0 0 10px -4px rgba(0, 0, 0, 0.5);
}
</style>
